<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-slate-900/40" @click="closePanel"></div>
      <aside class="absolute right-0 top-0 flex h-full w-full max-w-[460px] flex-col bg-white shadow-2xl">
        <header class="border-b border-slate-200 px-5 py-4">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-slate-900">编辑链路节点</h3>
              <p class="mt-1 text-sm text-slate-500">
                {{ isPlatform ? '平台节点仅支持锁定品名' : '手工值优先生效，留空使用系统值' }}
              </p>
            </div>
            <button type="button" class="table-action-button is-neutral" title="关闭" aria-label="关闭" @click="closePanel">
              <XMarkIcon class="h-4 w-4" />
            </button>
          </div>
        </header>

        <form class="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4" @submit.prevent="submitForm">
          <section class="rounded-lg border border-slate-200 p-4">
            <div class="flex flex-wrap items-center gap-2">
              <span :class="['status-badge', isPlatform ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600']">
                {{ isPlatform ? '平台' : roleLabel(node?.nodeRole || node?.role) }}
              </span>
              <span class="status-badge bg-blue-50 text-blue-700">链路 {{ chain?.chainNo || '-' }}</span>
            </div>
            <p v-if="node?.invalidReason" class="mt-2 text-xs font-medium text-red-600">{{ node.invalidReason }}</p>
          </section>

          <label class="block">
            <span class="form-label">公司名</span>
            <input
              v-model.trim="form.manualCompanyName"
              class="form-input mt-1 w-full"
              :placeholder="systemCompanyName || '留空使用系统公司名'"
              :disabled="isPlatform"
            />
            <p v-if="effectiveCompanyName" class="mt-1 text-xs text-slate-500">当前生效值：{{ effectiveCompanyName }}</p>
            <p v-if="systemCompanyName" class="mt-0.5 text-xs text-slate-400">系统值：{{ systemCompanyName }}</p>
          </label>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label class="block">
              <span class="form-label">进项金额</span>
              <input
                v-model.trim="form.manualInputAmount"
                class="form-input mt-1 w-full"
                inputmode="decimal"
                placeholder="100 元整数倍"
                :disabled="isPlatform"
              />
              <p v-if="effectiveInputAmount" class="mt-1 text-xs text-slate-500">当前生效值：{{ formatInvoiceAmount(effectiveInputAmount) }}</p>
              <p v-if="systemInputAmount" class="mt-0.5 text-xs text-slate-400">系统值：{{ formatInvoiceAmount(systemInputAmount) }}</p>
            </label>
            <label class="block">
              <span class="form-label">销项金额</span>
              <input
                v-model.trim="form.manualOutputAmount"
                class="form-input mt-1 w-full"
                inputmode="decimal"
                placeholder="100 元整数倍"
                :disabled="isPlatform"
              />
              <p v-if="effectiveOutputAmount" class="mt-1 text-xs text-slate-500">当前生效值：{{ formatInvoiceAmount(effectiveOutputAmount) }}</p>
              <p v-if="systemOutputAmount" class="mt-0.5 text-xs text-slate-400">系统值：{{ formatInvoiceAmount(systemOutputAmount) }}</p>
            </label>
          </div>

          <label class="block">
            <span class="form-label">利润率（%）</span>
            <input
              v-model.trim="form.manualProfitRatePercent"
              class="form-input mt-1 w-full"
              inputmode="decimal"
              placeholder="例如 6.50"
              :disabled="isPlatform"
            />
            <p v-if="effectiveProfitRate !== null" class="mt-1 text-xs text-slate-500">当前生效值：{{ formatProfitRate(effectiveProfitRate) }}</p>
            <p v-if="systemProfitRate !== null" class="mt-0.5 text-xs text-slate-400">系统值：{{ formatProfitRate(systemProfitRate) }}</p>
          </label>

          <label class="block">
            <span class="form-label">品名</span>
            <input
              v-model.trim="form.productName"
              class="form-input mt-1 w-full"
              :placeholder="systemProductName || '请输入开票品名'"
            />
            <p v-if="systemProductName" class="mt-1 text-xs text-slate-500">当前生效值：{{ systemProductName }}</p>
          </label>

          <section class="rounded-lg border border-slate-200 p-4">
            <h4 class="text-sm font-semibold text-slate-900">字段锁定</h4>
            <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label v-for="item in lockFields" :key="item.key" class="flex items-center gap-2 text-sm text-slate-700">
                <input v-model="form[item.key]" type="checkbox" class="form-check" />
                {{ item.label }}
              </label>
            </div>
          </section>
        </form>

        <footer class="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" class="btn-secondary" @click="closePanel">取消</button>
          <button type="button" class="btn-primary" v-permission="'enterprise-invoice:chain:edit'" @click="submitForm">保存</button>
        </footer>
      </aside>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { formatInvoiceAmount, formatProfitRate, normalizeAmountInput, parsePercentInput, roleLabel, toPercentInput } from '../utils'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  context: { type: Object, default: null },
  nodeContext: { type: Object, default: null }
})

const emit = defineEmits(['update:modelValue', 'saved', 'submit'])

const form = reactive({
  manualCompanyName: '',
  manualInputAmount: '',
  manualOutputAmount: '',
  manualProfitRatePercent: '',
  productName: '',
  companyLocked: false,
  inputAmountLocked: false,
  outputAmountLocked: false,
  profitRateLocked: false,
  productNameLocked: false
})

const initialNodePayload = reactive({
  manualCompanyName: null,
  manualInputAmount: null,
  manualOutputAmount: null,
  manualProfitRate: null,
  productName: null
})

const lockFields = [
  { key: 'companyLocked', label: '锁定公司名' },
  { key: 'inputAmountLocked', label: '锁定进项' },
  { key: 'outputAmountLocked', label: '锁定销项' },
  { key: 'profitRateLocked', label: '锁定利润率' },
  { key: 'productNameLocked', label: '锁定品名' }
]

const activeContext = computed(() => props.context || props.nodeContext || null)
const chain = computed(() => activeContext.value?.chain || null)
const node = computed(() => activeContext.value?.node || null)
const isPlatform = computed(() => node.value?.nodeRole === 'PLATFORM' || node.value?.role === 'PLATFORM' || node.value?.platform === true)
const systemCompanyName = computed(() => firstValue(node.value?.systemCompanyName, node.value?.companyNameSnapshot, node.value?.companyName))
const manualCompanyName = computed(() => firstValue(node.value?.manualCompanyName))
const effectiveCompanyName = computed(() => firstValue(node.value?.effectiveCompanyName, manualCompanyName.value, systemCompanyName.value))
const systemInputAmount = computed(() => firstValue(node.value?.systemInputAmount, node.value?.inputAmount, node.value?.inputInvoiceAmount))
const manualInputAmount = computed(() => firstValue(node.value?.manualInputAmount))
const effectiveInputAmount = computed(() => firstValue(node.value?.effectiveInputAmount, manualInputAmount.value, systemInputAmount.value))
const systemOutputAmount = computed(() => firstValue(node.value?.systemOutputAmount, node.value?.outputAmount, node.value?.outputInvoiceAmount))
const manualOutputAmount = computed(() => firstValue(node.value?.manualOutputAmount))
const effectiveOutputAmount = computed(() => firstValue(node.value?.effectiveOutputAmount, manualOutputAmount.value, systemOutputAmount.value))
const systemProfitRate = computed(() => firstValue(node.value?.systemProfitRate, node.value?.profitRate) || null)
const manualProfitRate = computed(() => firstValue(node.value?.manualProfitRate) || null)
const effectiveProfitRate = computed(() => firstValue(node.value?.effectiveProfitRate, manualProfitRate.value, systemProfitRate.value) || null)
const systemProductName = computed(() => firstValue(node.value?.productName))

function firstValue(...values) {
  return values.find(value => value !== null && value !== undefined && value !== '') ?? ''
}

function cleanText(value) {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  return text === '' ? null : text
}

function amountOrNull(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null
  return normalizeAmountInput(value)
}

function resetForm() {
  const current = node.value || {}
  initialNodePayload.manualCompanyName = cleanText(manualCompanyName.value)
  initialNodePayload.manualInputAmount = amountOrNull(manualInputAmount.value)
  initialNodePayload.manualOutputAmount = amountOrNull(manualOutputAmount.value)
  initialNodePayload.manualProfitRate = manualProfitRate.value ?? null
  initialNodePayload.productName = cleanText(firstValue(current.manualProductName, current.productName))

  form.manualCompanyName = initialNodePayload.manualCompanyName || ''
  form.manualInputAmount = initialNodePayload.manualInputAmount ?? ''
  form.manualOutputAmount = initialNodePayload.manualOutputAmount ?? ''
  form.manualProfitRatePercent = toPercentInput(initialNodePayload.manualProfitRate)
  form.productName = initialNodePayload.productName || ''
  form.companyLocked = Boolean(current.companyLocked)
  form.inputAmountLocked = Boolean(current.inputAmountLocked)
  form.outputAmountLocked = Boolean(current.outputAmountLocked)
  form.profitRateLocked = Boolean(current.profitRateLocked)
  form.productNameLocked = Boolean(current.productNameLocked)
}

function closePanel() {
  emit('update:modelValue', false)
}

function parseAmountField(value, label) {
  const text = value === null || value === undefined ? '' : String(value).trim()
  if (text === '') return null
  const amount = Number(text)
  if (!Number.isFinite(amount) || amount <= 0) {
    ElMessage.warning(`${label}必须为正数`)
    return undefined
  }
  if (amount % 100 !== 0) {
    ElMessage.warning(`${label}必须为 100 元整数倍`)
    return undefined
  }
  return normalizeAmountInput(amount)
}

function submitForm() {
  if (!activeContext.value || !node.value) return
  if (isPlatform.value && (form.manualCompanyName || form.manualInputAmount || form.manualOutputAmount || form.manualProfitRatePercent)) {
    ElMessage.warning('平台节点不能提交公司、金额或利润率手工修改')
    return
  }

  const manualInputAmount = isPlatform.value ? null : parseAmountField(form.manualInputAmount, '进项金额')
  if (manualInputAmount === undefined) return
  const manualOutputAmount = isPlatform.value ? null : parseAmountField(form.manualOutputAmount, '销项金额')
  if (manualOutputAmount === undefined) return

  const manualProfitRate = isPlatform.value ? null : parsePercentInput(form.manualProfitRatePercent)
  if (!isPlatform.value && form.manualProfitRatePercent && manualProfitRate === null) {
    ElMessage.warning('利润率必须为数字')
    return
  }

  const nodePayload = {
    manualCompanyName: isPlatform.value ? null : cleanText(form.manualCompanyName),
    manualInputAmount,
    manualOutputAmount,
    manualProfitRate,
    productName: cleanText(form.productName)
  }
  const clearManualFields = []
  if (!isPlatform.value && initialNodePayload.manualCompanyName !== null && nodePayload.manualCompanyName === null) {
    clearManualFields.push('manualCompanyName')
  }
  if (!isPlatform.value && initialNodePayload.manualInputAmount !== null && manualInputAmount === null) {
    clearManualFields.push('manualInputAmount')
  }
  if (!isPlatform.value && initialNodePayload.manualOutputAmount !== null && manualOutputAmount === null) {
    clearManualFields.push('manualOutputAmount')
  }
  if (!isPlatform.value && initialNodePayload.manualProfitRate !== null && manualProfitRate === null) {
    clearManualFields.push('manualProfitRate')
  }
  if (clearManualFields.length > 0) {
    nodePayload.clearManualFields = clearManualFields
  }
  const hasNodeChanges = !isPlatform.value && (
    nodePayload.manualCompanyName !== initialNodePayload.manualCompanyName ||
    nodePayload.manualInputAmount !== initialNodePayload.manualInputAmount ||
    nodePayload.manualOutputAmount !== initialNodePayload.manualOutputAmount ||
    nodePayload.manualProfitRate !== initialNodePayload.manualProfitRate ||
    nodePayload.productName !== initialNodePayload.productName ||
    clearManualFields.length > 0
  )

  const payload = {
    chain: chain.value,
    node: node.value,
    nodePayload: hasNodeChanges ? nodePayload : null,
    locks: {
      companyLocked: form.companyLocked,
      inputAmountLocked: form.inputAmountLocked,
      outputAmountLocked: form.outputAmountLocked,
      profitRateLocked: form.profitRateLocked,
      productNameLocked: form.productNameLocked
    }
  }
  emit('saved', payload)
  emit('submit', payload)
}

watch([activeContext, () => props.modelValue], () => {
  if (props.modelValue) resetForm()
}, { immediate: true })
</script>
